import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, CheckCircle2, AlertCircle, Share2, Copy } from "lucide-react";
import { BusyLabel, HitLoadingBlock, HitRing } from "@/components/loading";
import { toast } from "sonner";
import ShareCatalogModal from "@/components/ShareCatalogModal";

/**
 * Tarjeta de "Mi enlace público" en el perfil del vendedor.
 *
 * - Muestra el slug actual (auto-migrado si es cuenta nueva).
 * - Permite personalizarlo con validación live (chequea disponibilidad).
 * - Cooldown de 30 días entre cambios (bloqueado con mensaje claro).
 * - Botón compartir → abre modal QR.
 */
export default function MySlugCard({ user }) {
  const [slug, setSlug] = useState(null);
  const [cooldown, setCooldown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);  // {available, reason?, message?, suggestions?}
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/slug");
      setSlug(r.data?.slug || null);
      setCooldown(r.data?.cooldown || null);
    } catch {
      /* silencioso */
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced availability check
  useEffect(() => {
    if (!editing || !draft || draft === slug) {
      setCheckResult(null);
      return;
    }
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await api.get(`/slug/check?value=${encodeURIComponent(draft)}`);
        setCheckResult(r.data);
      } catch {
        setCheckResult({ available: false, message: "No se pudo validar" });
      } finally { setChecking(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, editing, slug]);

  const startEdit = () => {
    setDraft(slug || "");
    setCheckResult(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft("");
    setCheckResult(null);
  };

  const save = async () => {
    if (!draft || draft === slug) { cancelEdit(); return; }
    if (checkResult && !checkResult.available) return;
    setSaving(true);
    try {
      const r = await api.put("/me/slug", { slug: draft });
      setSlug(r.data?.slug || draft);
      toast.success("Enlace actualizado");
      cancelEdit();
      // Refresca cooldown
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || {};
      if (d.code === "cooldown") {
        toast.error(d.message || "Aún estás en cooldown de 30 días.");
        load();
      } else if (d.code === "taken") {
        setCheckResult({ available: false, message: d.message, suggestions: d.suggestions });
      } else if (d.message) {
        toast.error(d.message);
      } else {
        toast.error("No se pudo guardar el enlace");
      }
    } finally { setSaving(false); }
  };

  const publicUrl = slug ? `${window.location.origin}/tienda/${slug}` : null;

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Enlace copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  if (loading) {
    return (
      <HitLoadingBlock section="profile" />
    );
  }

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="my-slug-card">
      <div className="flex items-center gap-2 mb-2">
        <Link2 size={20} className="text-[#EF4444]" />
        <h2 className="font-display text-2xl">Mi enlace público</h2>
      </div>
      <p className="text-sm text-neutral-400 mb-4">
        Este es el enlace corto de tu catálogo. Compartelo por WhatsApp, imprímelo como QR o pégalo en tus redes.
        {!editing && (slug || "").startsWith("vendedor-") && (
          <> <b className="text-white">Personalízalo</b> para que sea más fácil de recordar (ej: <code className="text-[#EF4444]">maria-tcg</code>, <code className="text-[#EF4444]">panda-cards-ec</code>).</>
        )}
      </p>

      {!editing ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-sm text-white truncate select-all font-mono"
              data-testid="my-slug-current"
            >
              {publicUrl || "(sin slug asignado)"}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              onClick={() => setShareOpen(true)}
              className="bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="my-slug-share"
              disabled={!slug}
            >
              <Share2 size={16} className="mr-2" />Compartir con QR
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={copyUrl}
              className="border-[#262626] hover:bg-[#0A0A0A]"
              data-testid="my-slug-copy"
              disabled={!slug}
            >
              <Copy size={16} className="mr-2" />Copiar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={startEdit}
              className="border-[#262626] hover:bg-[#0A0A0A]"
              data-testid="my-slug-edit"
              disabled={!!cooldown}
            >
              Personalizar enlace
            </Button>
          </div>
          {cooldown && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-xs text-amber-200" data-testid="my-slug-cooldown">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div>
                Ya cambiaste tu enlace recientemente. Podrás cambiarlo de nuevo en <b>{cooldown.days_remaining} día{cooldown.days_remaining === 1 ? "" : "s"}</b>.
                Los enlaces viejos siguen funcionando (redirigen automáticamente).
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-slug-form">
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider">Nuevo enlace</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-neutral-500 text-sm font-mono">hittcg.store/tienda/</span>
              <Input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                maxLength={40}
                className="bg-[#171717] border-[#262626] font-mono flex-1"
                placeholder="mi-tienda"
                data-testid="my-slug-input"
              />
            </div>
            <div className="text-[11px] text-neutral-500 mt-2">
              3-40 caracteres · solo minúsculas, números y guiones · empieza y termina con letra o número.
            </div>
            {/* Feedback live */}
            <div className="mt-2 min-h-[24px] text-xs">
              {checking && (
                <span className="text-neutral-500 flex items-center gap-1"><HitRing tone="light" size={12} /> Validando…</span>
              )}
              {!checking && checkResult && checkResult.available && (
                <span className="text-emerald-400 flex items-center gap-1" data-testid="my-slug-available">
                  <CheckCircle2 size={12} /> Disponible
                </span>
              )}
              {!checking && checkResult && !checkResult.available && (
                <div className="text-red-400" data-testid="my-slug-unavailable">
                  <div className="flex items-center gap-1">
                    <AlertCircle size={12} /> {checkResult.message || "No disponible"}
                  </div>
                  {checkResult.suggestions && checkResult.suggestions.length > 0 && (
                    <div className="mt-1 text-neutral-400">
                      Alternativas:{" "}
                      {checkResult.suggestions.map((s, i) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setDraft(s)}
                          className="text-[#EF4444] hover:underline mx-1"
                          data-testid={`my-slug-suggestion-${i}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={save}
              disabled={saving || checking || (checkResult && !checkResult.available) || !draft || draft === slug}
              className="bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="my-slug-save"
            >
              <BusyLabel busy={saving} text="Guardando…">Guardar</BusyLabel>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={cancelEdit}
              className="border-[#262626] hover:bg-[#0A0A0A]"
              data-testid="my-slug-cancel"
            >
              Cancelar
            </Button>
          </div>
          <div className="text-[11px] text-neutral-500">
            Al cambiar tu enlace se activa un <b className="text-neutral-300">cooldown de 30 días</b> antes del próximo cambio. Los enlaces viejos siguen funcionando (redirigen al nuevo).
          </div>
        </div>
      )}

      <ShareCatalogModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sellerName={user?.name || "mi catálogo"}
        slug={slug}
      />
    </div>
  );
}
